import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  fetchPreferences,
  updatePreference,
} from '../../store/notificationsSlice';
import { requestPushPermission } from '../../utils/pushManager';

interface NotificationCenterProps {
  onClose: () => void;
  onNavigateTab: (tab: 'home' | 'deposit' | 'chat' | 'profile') => void;
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({ onClose, onNavigateTab }) => {
  const dispatch = useAppDispatch();
  const { items, preferences, loading } = useAppSelector((state) => state.notifications);
  const { workspaceId } = useAppSelector((state) => state.auth);
  const [activeSubTab, setActiveSubTab] = useState<'inbox' | 'preferences'>('inbox');

  useEffect(() => {
    dispatch(fetchNotifications());
    dispatch(fetchPreferences());
  }, []);

  const handleNotificationClick = (item: any) => {
    if (!item.read) {
      dispatch(markNotificationRead(item.id));
    }
    const type = item.type;
    const actionUrl = item.actionUrl;

    if (type === 'message' || actionUrl?.includes('/chat')) {
      onNavigateTab('chat');
      onClose();
    } else if (type === 'deposit' || actionUrl?.includes('deposit')) {
      onNavigateTab('deposit');
      onClose();
    }
  };

  const handleMarkAllRead = () => {
    dispatch(markAllNotificationsRead());
  };

  const handlePreferenceToggle = (category: string, channel: 'IN_APP' | 'PUSH', enabled: boolean) => {
    dispatch(updatePreference({ category, channel, enabled }));
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case 'message': return '💬';
      case 'action': return '⚡️';
      case 'alert': return '⚠️';
      default: return '⚙️';
    }
  };

  const getPreferenceValue = (category: string, channel: 'IN_APP' | 'PUSH'): boolean => {
    const pref = preferences.find(p => p.category === category && p.channel === channel);
    return pref ? pref.enabled : true;
  };

  const handleEnableWebPush = async () => {
    const granted = await requestPushPermission(workspaceId);
    if (granted) {
      alert('Web Push notifications enabled successfully!');
    } else {
      alert('Notification permissions were not granted by browser settings.');
    }
  };

  const unreadCount = items.filter((i) => !i.read).length;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ width: '100%', maxWidth: '400px', height: '80vh', backgroundColor: '#12121e', borderRadius: '20px', border: '1px solid rgba(0, 255, 204, 0.2)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#00ffcc', letterSpacing: '1px' }}>
            Console Center
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '20px', cursor: 'pointer' }}>
            ✕
          </button>
        </div>

        {/* Sub-Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <button
            onClick={() => setActiveSubTab('inbox')}
            style={{ flex: 1, padding: '12px', background: 'none', border: 'none', borderBottom: activeSubTab === 'inbox' ? '2px solid #00ffcc' : 'none', color: activeSubTab === 'inbox' ? '#00ffcc' : 'rgba(255,255,255,0.6)', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}
          >
            Inbox {unreadCount > 0 && `(${unreadCount})`}
          </button>
          <button
            onClick={() => setActiveSubTab('preferences')}
            style={{ flex: 1, padding: '12px', background: 'none', border: 'none', borderBottom: activeSubTab === 'preferences' ? '2px solid #00ffcc' : 'none', color: activeSubTab === 'preferences' ? '#00ffcc' : 'rgba(255,255,255,0.6)', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}
          >
            Preferences
          </button>
        </div>

        {/* Web Push Prompt Banner */}
        <div style={{ padding: '10px 16px', background: 'rgba(139, 92, 246, 0.1)', borderBottom: '1px solid rgba(139, 92, 246, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: '11px', color: '#a78bfa', fontWeight: '600' }}>
            Enable iOS Web Push Alerts
          </div>
          <button
            onClick={handleEnableWebPush}
            style={{ background: '#8B5CF6', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: '800', cursor: 'pointer' }}
          >
            ENABLE
          </button>
        </div>

        {/* Content */}
        {activeSubTab === 'inbox' ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {items.length > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{ alignSelf: 'flex-end', padding: '8px 16px', background: 'none', border: 'none', color: '#00ffcc', fontSize: '12px', cursor: 'pointer' }}
              >
                ✓ Mark all as read
              </button>
            )}

            <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {items.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleNotificationClick(item)}
                  style={{ backgroundColor: item.read ? '#1b1b2d' : '#20203a', border: `1px solid ${item.read ? 'rgba(255,255,255,0.05)' : 'rgba(0, 255, 204, 0.25)'}`, borderRadius: '12px', padding: '14px', cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{getIconForType(item.type)}</span>
                      <span style={{ fontSize: '14px', fontWeight: item.read ? '500' : 'bold', color: item.read ? '#ffffff' : '#00ffcc' }}>
                        {item.title}
                      </span>
                    </div>
                    {!item.read && <div style={{ width: '8px', height: '8px', borderRadius: '4px', backgroundColor: '#00ffcc' }} />}
                  </div>

                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', lineHeight: '1.4', marginBottom: '6px' }}>
                    {item.message}
                  </div>

                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textAlign: 'right' }}>
                    {item.created_at ? new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Now'}
                  </div>
                </div>
              ))}

              {items.length === 0 && (
                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', marginTop: '40px', fontSize: '14px' }}>
                  No notifications yet.
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#fff', marginBottom: '14px' }}>
              Channel Subscriptions
            </div>

            {/* Chat Messages */}
            <div style={{ backgroundColor: '#1b1b2d', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px', padding: '14px', marginBottom: '14px' }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#fff', marginBottom: '4px' }}>💬 Chat & Support Messages</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '10px' }}>Configure notifications for live support conversation replies.</div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>In-App Notifications</span>
                <input
                  type="checkbox"
                  checked={getPreferenceValue('chat', 'IN_APP')}
                  onChange={(e) => handlePreferenceToggle('chat', 'IN_APP', e.target.checked)}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>Push Notifications</span>
                <input
                  type="checkbox"
                  checked={getPreferenceValue('chat', 'PUSH')}
                  onChange={(e) => handlePreferenceToggle('chat', 'PUSH', e.target.checked)}
                />
              </div>
            </div>

            {/* System Updates */}
            <div style={{ backgroundColor: '#1b1b2d', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px', padding: '14px', marginBottom: '14px' }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#fff', marginBottom: '4px' }}>⚙️ System & Account Updates</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '10px' }}>Configure notifications for account suspension status or administrative revisions.</div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>In-App Notifications</span>
                <input
                  type="checkbox"
                  checked={getPreferenceValue('system', 'IN_APP')}
                  onChange={(e) => handlePreferenceToggle('system', 'IN_APP', e.target.checked)}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>Push Notifications</span>
                <input
                  type="checkbox"
                  checked={getPreferenceValue('system', 'PUSH')}
                  onChange={(e) => handlePreferenceToggle('system', 'PUSH', e.target.checked)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
